import { useCallback, useMemo, useRef } from 'react'

import {
  ResolvedRecordType,
  Select,
  SelectItemProps,
} from '@factorialco/f0-react/dist/experimental'
import { Search } from '@factorialco/f0-react/icons/app'

import { useGraphqlDataSource } from 'modules/core/hooks/useGraphqlDataSource'
import { useQuery } from 'modules/core/hooks/useQuery'
import i18n from 'modules/core/lib/i18n'

import { EntitySelectorV2Props } from './types'

export const EntitySelectorV2 = <
  ListResult,
  SingleItemResult,
  R,
  ListVariables extends { [key: string]: unknown },
  ForcedFiltersType extends { [key: string]: unknown }
>({
  document,
  singleItemDocument,
  extractConnection,
  extractSingleItem,
  mapItemTitle,
  mapItemAvatar,
  label,
  hint,
  messages,
  options,
  disabled,
  filters,
  forcedFilters,
  error,
  mapFiltersToVariables,
  value,
  ...props
}: EntitySelectorV2Props<ListResult, SingleItemResult, R, ListVariables, ForcedFiltersType>) => {
  const defaultValue = useRef(value).current

  const { data: singleItemData } = useQuery(singleItemDocument, {
    fetchPolicy: 'cache-first',
    variables: {
      id: typeof defaultValue === 'number' ? defaultValue : -1,
    },
    skip: typeof defaultValue !== 'number',
  })

  const singleItem = useMemo(() => {
    if (typeof defaultValue !== 'number' || !singleItemData) {
      return undefined
    }

    return extractSingleItem(singleItemData)
  }, [singleItemData, extractSingleItem, defaultValue])

  const source = useGraphqlDataSource(
    {
      paginationType: 'infinite-scroll',
      fetchPolicy: 'cache-first',
      document,
      filters,
      mapData: (data) => extractConnection(data).edges.map((edge) => edge.node),
      mapPagination: (data) => {
        const connection = extractConnection(data)
        const pageInfo = connection.pageInfo
        const hasNextPage = !!pageInfo.hasNextPage

        const total = connection.totalCount ?? 0

        return {
          total,
          cursor: pageInfo.endCursor ?? '0',
          hasNextPage,
        }
      },
      selectable: (item) => String(item.id),
      mapVariables: ({
        search,
        filters: filtersState,
      }): Omit<ListVariables, 'cursor' | 'perPage' | 'page'> => {
        const variables = mapFiltersToVariables({ forcedFilters, filters: filtersState })

        return {
          ...variables,
          search,
        }
      },
    },
    [filters]
  )

  const mapOptions = useCallback(
    (
      item: ResolvedRecordType<R>
    ): SelectItemProps<string, ResolvedRecordType<R>> & { type: 'item' } => ({
      value: String(item.id),
      label: mapItemTitle(item),
      avatar: mapItemAvatar?.(item),
      type: 'item',
    }),
    [mapItemTitle, mapItemAvatar]
  )

  const handleChange = useCallback(
    (val: string | undefined) => {
      if (typeof val !== 'string' || !val) {
        props.onChange(undefined)
        return
      }

      if (props.multiple) {
        props.onChange([Number(val)])
      } else {
        props.onChange(Number(val))
      }
    },
    [props]
  )

  const finalValue = useMemo(() => {
    if (Array.isArray(value)) {
      return String(value[0])
    }

    return value ? String(value) : undefined
  }, [value])

  const defaultItem = singleItem ? mapOptions(singleItem) : undefined

  return (
    <Select
      source={source}
      searchEmptyMessage={
        messages?.searchEmptyMessage ?? i18n.tp('employees.select_employee', { count: 0 })
      }
      defaultItem={defaultItem}
      icon={options?.disableSearch || options?.hideSearchIcon ? undefined : Search}
      label={label}
      hint={hint}
      size="md"
      placeholder={messages?.placeholder}
      searchBoxPlaceholder={messages?.searchBoxPlaceholder}
      onChange={handleChange}
      value={finalValue}
      disabled={disabled}
      mapOptions={mapOptions}
      error={error}
      hideLabel={options?.hideLabel}
      showSearchBox={!options?.disableSearch}
      clearable={!props.mandatory}
    />
  )
}
